// Consistent rendering of invoice status, flag chips, the aging pill, and the
// shared tinted dot-pill they build on (app.md §8).
import type { ReactNode } from 'react'
import { Badge, tokens } from '@fluentui/react-components'
import { InvoiceStatus, flagLabel, statusLabel } from '../lib/constants'
import { ageBand, flagPillLabel, uncodedLinesLabel } from '../lib/format'
import { AGE_PILL_COLORS, TINT_PILLS, type PillColors } from '../lib/theme'

type BadgeColor =
  | 'brand'
  | 'danger'
  | 'important'
  | 'informative'
  | 'severe'
  | 'subtle'
  | 'success'
  | 'warning'

const STATUS_COLOR: Record<string, BadgeColor> = {
  [InvoiceStatus.PendingMatching]: 'subtle',
  [InvoiceStatus.PendingImportControl]: 'warning',
  [InvoiceStatus.PendingController]: 'brand',
  [InvoiceStatus.PendingDepartment]: 'brand',
  [InvoiceStatus.PendingDirector]: 'brand',
  [InvoiceStatus.PendingOversight]: 'brand',
  [InvoiceStatus.PendingExportControl]: 'brand',
  [InvoiceStatus.PendingDenialReview]: 'severe',
  [InvoiceStatus.PendingExport]: 'informative',
  [InvoiceStatus.Imported]: 'success',
  [InvoiceStatus.Typed]: 'success',
  // The six archives (D-362).
  [InvoiceStatus.Blank]: 'danger',
  [InvoiceStatus.DifferentTypeOfDocument]: 'danger',
  [InvoiceStatus.Duplicate]: 'danger',
  [InvoiceStatus.PaymentDenied]: 'danger',
  [InvoiceStatus.RevisedInvoiceRequested]: 'danger',
  [InvoiceStatus.Other]: 'danger',
}

export function StatusBadge({ status }: { status: string | undefined }) {
  if (!status) return <Badge appearance="tint" color="subtle">Unknown</Badge>
  return (
    <Badge appearance="tint" color={STATUS_COLOR[status] ?? 'subtle'}>
      {/* Never the STORED `status` (owner item 8, 2026-08-19; D-449): the
          step's name, as the Filter's Status picker reads, or "Archives - …"
          for a terminal status. */}
      {statusLabel(status)}
    </Badge>
  )
}

/**
 * A flag as a chip — one named component, one call site
 * (`EditableInvoiceForm.tsx`'s flag list, beside the resolved flags'
 * `DotPill`). Renders as a `DotPill`, not a Fluent `Badge` (owner item 5,
 * 2026-08-18 — the two used to look like different sizes of chip in the same
 * list): `Badge size="small"` is 16px tall with 4px of horizontal padding and,
 * via Fluent's own `smallToTiny` mapping, a **10px** font — a resolved flag
 * turns into a `DotPill` at **12px** (`fontSizeBase200`), the same size as the
 * `Caption1` text sitting next to it in the row and the size `EntityPicker`'s
 * own field label states explicitly. `DotPill` is the one that already
 * matches the modal's type scale, so it is the shape that stays; the `Badge`
 * was the outlier and is the one that goes. Unresolved flags gain a colored
 * dot they didn't have and lose Fluent's 1px `::after` contrast ring — the
 * red foreground on the red tint (`TINT_PILLS.red`) still measures ~5.7:1,
 * comfortably past WCAG AA's 4.5:1 for normal text, so the ring isn't load-
 * bearing for legibility.
 */
export function FlagChip({ flag, label }: { flag: string | undefined; label?: string | null }) {
  // RED FOR EVERY UNRESOLVED FLAG (owner, 2026-09-25 — `docs/decisions.md`
  // D-149: the Amounts Don't Reconcile flag "looks different from most
  // flags"). An `auto` flag used to be yellow; it now looks and works like
  // every other flag, and turns green only when resolved.
  return (
    <DotPill pill={TINT_PILLS.red}>
      {flagLabel(flag, label)}
    </DotPill>
  )
}

/**
 * Plain gray pill, no dot and no semantic color (owner, 2026-08-28: the
 * per-group invoice count and total at Export Control and Closing
 * Confirmation — "format them as neutral, gray-shaded pills"). The dot on
 * `DotPill` carries a status; these two carry a measurement, so there is
 * nothing for a color to mean.
 */
export function NeutralPill({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '3px 10px',
        borderRadius: tokens.borderRadiusCircular,
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightSemibold,
        whiteSpace: 'nowrap',
        fontVariantNumeric: 'tabular-nums',
        color: tokens.colorNeutralForeground2,
        backgroundColor: tokens.colorNeutralBackground3,
      }}
      title={title}
    >
      {children}
    </span>
  )
}

/** Tinted pill with a solid dot — the queue cards' shared chip shape. */
export function DotPill({ pill, title, children }: { pill: PillColors; title?: string; children: ReactNode }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '3px 10px 3px 8px',
        borderRadius: tokens.borderRadiusCircular,
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightSemibold,
        whiteSpace: 'nowrap',
        color: pill.fg,
        backgroundColor: pill.bg,
      }}
      title={title}
    >
      <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: pill.dot, flexShrink: 0 }} />
      {children}
    </span>
  )
}

/** "5 days" pill colored by aging band, green (fresh) → red (7+ days); always
    shows the day count so it reads without color. */
export function AgePill({ days }: { days: number | null }) {
  if (days === null) return <span style={{ color: tokens.colorNeutralForeground3 }}>—</span>
  return (
    <DotPill pill={AGE_PILL_COLORS[ageBand(days)]} title={`${days} day${days === 1 ? '' : 's'} since invoice date`}>
      {days} day{days === 1 ? '' : 's'}
    </DotPill>
  )
}

/**
 * "3 flags" (red) / "No flags" (green) — the Import Control flags
 * pill. The count is still the UNRESOLVED count; the word "unresolved" left
 * the pill because a resolved flag is not something the card needs to say
 * (owner, 2026-08-11). The tooltip still lists the flag labels.
 */
export function UnresolvedFlagsPill({ count, title }: { count: number; title?: string }) {
  return (
    <DotPill pill={count > 0 ? TINT_PILLS.red : TINT_PILLS.green} title={title}>
      {flagPillLabel(count)}
    </DotPill>
  )
}

/**
 * "3 line items need action" — the Department / Director / Oversight Approval
 * coding-gate pill (owner, 2026-08-17). The COUNT is passed in by the caller,
 * resolved through the same `uncodedCount` helper that disables the Approve
 * button (`ApprovalScreen`, D-9) rather than read straight off
 * `uncoded_line_item_count` here, so the number on the pill is the number the
 * button is gated on.
 *
 * The TOOLTIP is not the same guarantee, and the distinction is worth keeping
 * straight: the button's title is `heldTitle ?? uncodedReason(inv)`, so on a
 * held-and-uncoded row the button explains the hold while this pill explains
 * the coding. Both true, deliberately different.
 *
 * Amber, not red: nothing is wrong, there is work left to do — the same
 * reasoning as the bulk caption at `ApprovalScreen.tsx`'s `uncoded` style,
 * though not the same token (that one is `colorPaletteDarkOrangeForeground1`).
 *
 * Nothing at zero. At this step zero uncoded lines is just the precondition
 * for Approve already being enabled, not a finding worth a pill — so the
 * guard is HERE, not a rule callers are trusted to remember.
 */
export function UncodedLinesPill({ count, title }: { count: number; title?: string }) {
  if (count <= 0) return null
  return (
    <DotPill pill={TINT_PILLS.yellow} title={title}>
      {uncodedLinesLabel(count)}
    </DotPill>
  )
}
